import{QueryClient,QueryCache,MutationCache}from'@tanstack/react-query';import{isHttpError,isRateLimitSignal}from'@/lib/http/error';import{rateLimitGate}from'@/lib/query/rateLimitGate';/**
 * True when an error represents an upstream rate limit, regardless of how it
 * was encoded (HTTP 429, wrapped 429, rate_limit category, or "rate limit"
 * text). RLE on logged-in 1P GraphQL surfaces as HTTP 200-with-errors, which
 * the ingredients route re-maps to a 429 — so on the client it arrives as an
 * HttpError that isRateLimitSignal recognizes.
 */function isRateLimitHttpError(error:unknown):boolean{return isHttpError(error)&&isRateLimitSignal(error);}/**
 * Creates a QueryClient with sensible defaults for the Midjen app.
 *
 * Uses a "stale-while-revalidate" pattern (like Relay's store-and-network):
 * - Shows cached data immediately (no loading spinner on navigation)
 * - Refetches in background to check for updates
 * - Updates UI when fresh data arrives
 *
 * - staleTime: 0 - data is immediately stale (always refetch on mount)
 * - gcTime: 5 minutes - unused cache entries are garbage collected after 5m
 * - refetchOnMount: true - refetch when component mounts (default)
 * - retry: 1, EXCEPT rate-limit errors (retrying a rate limit just burns budget)
 * - refetchOnWindowFocus: false - don't refetch on tab focus (can be noisy)
 * - refetchOnReconnect: suppressed while rate-limited — see the rate-limit gate
 *
 * Rate-limit gate (MIDJEN-5CS): a flapping connection refetches every mounted
 * query on each reconnect; once RLE starts blocking, the errored queries stay
 * refetch-eligible and re-fire forever with no brake. The QueryCache onError
 * detects rate-limit errors and trips `rateLimitGate` — a self-clearing client
 * brake. While it's engaged, `refetchOnReconnect` returns false and rate-limit
 * errors aren't retried, so the flapping-reconnect refetch storm can't sustain
 * itself (this gates reconnect + retry — not invalidateQueries polling or
 * mount-refetch). The brake is SILENT and independent of the generation
 * cooldown UI: RLE counters are per-endpoint, so a background data-read throttle
 * does not mean generation is throttled (~97% decoupled, MIDJEN-5CS).
 */export function createQueryClient():QueryClient{return new QueryClient({queryCache:new QueryCache({onError:(error:unknown)=>{// Any query that fails with a rate limit trips the silent brake (not
// just ingredients). Gates reconnect-refetch + retry; invalidateQueries
// polling and mount-refetch fire independently of the brake.
if(isRateLimitHttpError(error)){rateLimitGate.notifyRateLimitError();}}}),mutationCache:new MutationCache(),defaultOptions:{queries:{staleTime:0,// Data is immediately stale - always refetch on mount
gcTime:5*60*1000,// 5 minutes (formerly cacheTime)
refetchOnMount:true,// Refetch when component mounts (shows cached first)
// Preserve the previous `retry: 1` for ordinary failures, but never
// retry a rate limit — a retry just spends more of the user's budget.
retry:(failureCount,error)=>{if(isRateLimitHttpError(error))return false;return failureCount<1;},refetchOnWindowFocus:false,// While a rate-limit cooldown is active, do NOT refetch on reconnect.
// This is the brake that stops the flapping-connection refetch storm.
refetchOnReconnect:()=>!rateLimitGate.isRateLimited()},mutations:{retry:0}}});}